import {
  useListBulkReprocessRuns,
  type BulkReprocessRunDto,
  type ListBulkReprocessRunsParams,
} from '@sara/api-client';
import { Badge } from '@sara/ui/components/badge';
import { Button } from '@sara/ui/components/button';
import { DataPanel } from '@sara/ui/components/data-panel';
import { Skeleton } from '@sara/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sara/ui/components/table';
import { getApiErrorMessage } from '../../../../lib/api-error';
import { MODE_METADATA, RUN_STATUS_VARIANT } from './display';

interface BulkReprocessRunsListProps {
  params: ListBulkReprocessRunsParams;
  onSelectRun: (runId: string) => void;
  onPageChange: (page: number) => void;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function Row({
  run,
  onSelectRun,
}: {
  run: BulkReprocessRunDto;
  onSelectRun: (id: string) => void;
}) {
  return (
    <TableRow className="cursor-pointer" onClick={() => onSelectRun(run.id)}>
      <TableCell className="font-mono text-xs text-muted-foreground">
        {run.id.slice(0, 8)}
      </TableCell>
      <TableCell>{MODE_METADATA[run.mode].label}</TableCell>
      <TableCell>
        <Badge variant={RUN_STATUS_VARIANT[run.status]}>{run.status}</Badge>
      </TableCell>
      <TableCell className="tabular-nums">{run.totalCount}</TableCell>
      <TableCell className="tabular-nums text-success">{run.succeededCount}</TableCell>
      <TableCell className="tabular-nums text-destructive">{run.failedCount}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{formatDate(run.createdAt)}</TableCell>
    </TableRow>
  );
}

export function BulkReprocessRunsList({
  params,
  onSelectRun,
  onPageChange,
}: BulkReprocessRunsListProps) {
  const { data, isLoading, error, isFetching } = useListBulkReprocessRuns(params, {
    query: {
      // Poll while any run on this page is still working, so counts tick live.
      refetchInterval: (query) =>
        query.state.data?.items.some((r) => r.status === 'RUNNING' || r.status === 'PENDING')
          ? 4000
          : false,
    },
  });

  if (isLoading) {
    return (
      <div className="grid gap-2" data-testid="bulk-reprocess-runs-loading">
        <Skeleton className="h-10 rounded-lg" />
        <Skeleton className="h-10 rounded-lg" />
        <Skeleton className="h-10 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        {getApiErrorMessage(error) ?? 'Failed to load runs'}
      </div>
    );
  }

  const runs = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = data?.page ?? 1;
  const pageSize = data?.pageSize ?? 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-16 text-center">
        <p className="font-display text-lg font-medium text-foreground">No runs found.</p>
        <p className="mt-2 max-w-xs text-sm text-muted-foreground">
          Adjust the filters, or start a new bulk run.
        </p>
      </div>
    );
  }

  return (
    <DataPanel>
      <DataPanel.Body className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>ID</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Succeeded</TableHead>
              <TableHead>Failed</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.map((run) => (
              <Row key={run.id} run={run} onSelectRun={onSelectRun} />
            ))}
          </TableBody>
        </Table>
      </DataPanel.Body>
      <DataPanel.Footer className="flex items-center justify-between">
        <span className="tabular-nums text-muted-foreground">
          {total} {total === 1 ? 'run' : 'runs'}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1 || isFetching}
            onClick={() => onPageChange(page - 1)}
          >
            Prev
          </Button>
          <span className="tabular-nums text-[12.5px] text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= totalPages || isFetching}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </DataPanel.Footer>
    </DataPanel>
  );
}
